import React from "react"; // Required for JSX even if not explicitly used
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Send, ImageIcon, Phone, VideoIcon } from 'lucide-react';
import { ChatMessageItem as ChatMessage } from '@/components/chat-message';
import TopHeader  from "../components/TopHeader"; // Import Settings icon
import BottomNav  from "../components/BottomNav"; // Import Settings icon

interface Message {
  id: string;
  content: string;
  user: {
    id: string;
    name: string;
    avatar?: string;
  };
  createdAt: string;
}

const dummyMessages: Message[] = [
  {
    id: '1',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla turpis leo, sagittis vitae rhoncus vitae, fringilla non turpis. Aenean ligula augue, vestibulum ultrices ipsum a, auctor tincidunt dolor. Nullam felis ex, commodo vel suscipit et, sollicitudin id urna. Nam cursus.',
    user: { id: 'other-1', name: 'Other' },
    createdAt: '12:34PM',
  },
  {
    id: '2',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
    user: { id: 'me-1', name: 'Me' },
    createdAt: '12:31PM',
  },
  {
    id: '3',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla turpis leo, sagittis vitae rhoncus vitae, fringilla non turpis. Aenean ligula augue, vestibulum ultrices ipsum a, auctor tincidunt dolor. Nullam felis ex, commodo vel suscipit et.',
    user: { id: 'other-1', name: 'Other' },
    createdAt: '12:32PM',
  },
  {
    id: '4',
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla turpis leo, sagittis vitae rhoncus vitae, fringilla non turpis. Aenean ligula augue, vestibulum ultrices ipsum a, auctor tincidunt dolor. Nullam felis ex, commodo vel suscipit et, sollicitudin id urna. Nam cursus.',
    user: { id: 'me-1', name: 'Me' },
    createdAt: '12:34PM',
  },
];

export default function NewChatPage() {
  const [messages, setMessages] = useState<Message[]>(dummyMessages);
  const [inputText, setInputText] = useState('');

  const handleSend = () => {
    if (inputText.trim() === '') return;
    const newMessage: Message = {
      id: (messages.length + 1).toString(),
      content: inputText,
      user: { id: 'me-1', name: 'Me' },
      createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages([...messages, newMessage]);
    setInputText('');
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <TopHeader/>
      {/* Header with back button and user info */}
      <header className="flex items-center p-4 jr-bg-blue text-white space-x-4">
        <Avatar>
          <AvatarFallback>U</AvatarFallback>
        </Avatar>
        <div>
          <p className="font-semibold">Username</p>
          <p className="text-sm text-green-400">Online</p>
        </div>
        <div className="flex space-x-4 ml-auto">
          <Button variant="secondary" size="icon" aria-label="Send Image">
            <ImageIcon />
          </Button>
          <Button variant="secondary" size="icon" aria-label="Call">
            <Phone />
          </Button>
          <Button variant="secondary" size="icon" aria-label="Video Call">
            <VideoIcon />
          </Button>
        </div>
      </header>

      {/* Messages list */}
      <main className="flex-1 overflow-y-auto p-4 space-y-2">
        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} isOwnMessage={msg.user.name === 'Me'} showHeader={true} />
        ))}
      </main>

      {/* Input area */}
      <footer className="p-4 bg-white border-t flex items-center space-x-2">
        <Input
          type="text"
          placeholder="Type a message"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="flex-1"
        />
        <Button onClick={handleSend} aria-label="Send message">
          <Send />
        </Button>
      </footer>
      {/* Bottom Navigation */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t p-2 flex justify-around items-center text-gray-600 shadow-lg">
          <BottomNav/>
      </footer>
    </div>
  );
}
